import { createTheme } from '@mui/material/styles'

// Primary Color - Blue (#3467f6) - Consistent across both themes
const primaryColor = {
  50: '#e8ebff',
  100: '#c5ceff',
  200: '#9eadff',
  300: '#778cff',
  400: '#5a73ff',
  500: '#3467f6', // Main primary color (from design)
  600: '#2d5ef0',
  700: '#2554e8',
  800: '#1d4ae0',
  900: '#0f37d4',
  A100: '#ffffff',
  A200: '#f0f1ff',
  A400: '#bdc2ff',
  A700: '#a4abff',
}

// Secondary Color - Orange/Amber (for accents)
const secondaryColor = {
  50: '#fff8e6',
  100: '#ffedbf',
  200: '#ffe195',
  300: '#ffd56a',
  400: '#ffcc4a',
  500: '#ffc32a', // Main secondary color
  600: '#ffbd25',
  700: '#ffb51f',
  800: '#ffae19',
  900: '#ffa10f',
  A100: '#ffffff',
  A200: '#ffffff',
  A400: '#ffe8b3',
  A700: '#ffdd9a',
}

// Status Colors (from design)
const statusColors = {
  active: {
    light: {
      background: '#E6F6EA',
      text: '#3B8E42',
    },
    dark: {
      background: '#1B5E20',
      text: '#81C784',
    },
  },
  inactive: {
    light: {
      background: '#FFF8E6',
      text: '#C08E1D',
    },
    dark: {
      background: '#5D4037',
      text: '#FFB74D',
    },
  },
}

// Chart Colors (from design)
const chartColors = {
  purple: {
    main: '#6D28D9',
    light: '#A78BFA',
  },
  orange: {
    main: '#F97316',
    light: '#FB923C',
  },
  green: {
    main: '#22C55E',
    light: '#4ADE80',
  },
}

// Dark mode theme - Pure black background (as requested)
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: primaryColor[500],
      light: primaryColor[300],
      dark: primaryColor[700],
      contrastText: '#ffffff',
    },
    secondary: {
      main: secondaryColor[500],
      light: secondaryColor[300],
      dark: secondaryColor[700],
      contrastText: '#000000',
    },
    // Pure black background as requested
    background: {
      default: '#000000', // Pure black - page background & sidebar
      paper: '#121212', // Very dark gray for cards/panels (slight elevation)
      sidebar: '#000000', // Pure black for sidebar
      content: '#121212', // Dark gray for main content area
    },
    text: {
      primary: '#ffffff', // White text for primary content
      secondary: 'rgba(255, 255, 255, 0.7)', // Light gray for secondary text
      disabled: 'rgba(255, 255, 255, 0.5)', // Disabled text
      hint: 'rgba(255, 255, 255, 0.5)', // Hint text
      // Text colors for sidebar
      sidebar: {
        primary: '#ffffff', // White for active items
        secondary: 'rgba(255, 255, 255, 0.7)', // Light gray for inactive items
      },
    },
    divider: 'rgba(255, 255, 255, 0.12)', // Subtle divider
    // Status colors for dark mode
    status: {
      active: {
        background: statusColors.active.dark.background,
        text: statusColors.active.dark.text,
      },
      inactive: {
        background: statusColors.inactive.dark.background,
        text: statusColors.inactive.dark.text,
      },
    },
    // Chart colors
    chart: {
      purple: chartColors.purple.main,
      purpleLight: chartColors.purple.light,
      orange: chartColors.orange.main,
      orangeLight: chartColors.orange.light,
      green: chartColors.green.main,
      greenLight: chartColors.green.light,
    },
    error: {
      main: '#f44336',
      light: '#e57373',
      dark: '#d32f2f',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#ff9800',
      light: '#ffb74d',
      dark: '#f57c00',
      contrastText: '#000000',
    },
    info: {
      main: '#2196f3',
      light: '#64b5f6',
      dark: '#1976d2',
      contrastText: '#ffffff',
    },
    success: {
      main: '#4caf50',
      light: '#81c784',
      dark: '#388e3c',
      contrastText: '#ffffff',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontWeight: 600,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#000000', // Pure black
        },
      },
    },
    // Custom styling for better dark mode experience
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#121212',
        },
      },
    },
  },
})

// Light mode theme - bg white, font black (as requested)
const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: primaryColor[500],
      light: primaryColor[300],
      dark: primaryColor[700],
      contrastText: '#ffffff',
    },
    secondary: {
      main: secondaryColor[500],
      light: secondaryColor[300],
      dark: secondaryColor[700],
      contrastText: '#000000',
    },
    // Light mode: pure white background with dark text
    background: {
      default: '#ffffff', // Pure white - page background
      paper: '#ffffff', // White for cards/panels
      sidebar: '#ffffff', // White for sidebar
      content: '#F7F7F7', // Very light gray for main content area
      tableHeader: '#F5F7FA', // Very light gray for table headers (from design)
    },
    text: {
      primary: '#000000', // Pure black text for primary content (as requested)
      secondary: 'rgba(0, 0, 0, 0.6)', // Gray for secondary text
      disabled: 'rgba(0, 0, 0, 0.38)', // Disabled text
      hint: 'rgba(0, 0, 0, 0.38)', // Hint text
      // Text colors for sidebar
      sidebar: {
        primary: '#000000', // Pure black text on white sidebar
        secondary: 'rgba(0, 0, 0, 0.6)', // Secondary text on sidebar
      },
    },
    divider: 'rgba(0, 0, 0, 0.12)', // Subtle divider
    // Status colors for light mode (from design)
    status: {
      active: {
        background: statusColors.active.light.background,
        text: statusColors.active.light.text,
      },
      inactive: {
        background: statusColors.inactive.light.background,
        text: statusColors.inactive.light.text,
      },
    },
    // Chart colors (from design)
    chart: {
      purple: chartColors.purple.main,
      purpleLight: chartColors.purple.light,
      orange: chartColors.orange.main,
      orangeLight: chartColors.orange.light,
      green: chartColors.green.main,
      greenLight: chartColors.green.light,
    },
    error: {
      main: '#f44336',
      light: '#e57373',
      dark: '#d32f2f',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#ff9800',
      light: '#ffb74d',
      dark: '#f57c00',
      contrastText: '#000000',
    },
    info: {
      main: '#2196f3',
      light: '#64b5f6',
      dark: '#1976d2',
      contrastText: '#ffffff',
    },
    success: {
      main: '#4caf50',
      light: '#81c784',
      dark: '#388e3c',
      contrastText: '#ffffff',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontWeight: 600,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#ffffff', // Pure white page background
        },
      },
    },
    // Custom styling for better light mode experience
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
        },
      },
    },
  },
})

export default {
  dark: darkTheme,
  light: lightTheme,
}
